/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import PluginCard from "../ui/PluginCard";
import Icon from "../ui/Icon";
import Text from "../ui/Text";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import Button from "../ui/Button";
import FormatDatePeriod from "../formatters/FormatDatePeriod";
import FormatPrice from "../formatters/FormatPrice";
import CancelRecurringModal from "./CancelRecurringModal";
import { useTranslate } from "../../hooks/useTranslate";
import { ROUTES, RECURRING_PROFILE_STATUS } from "../../constants/constants";
import { Link } from "react-router-dom";
import styled from "styled-components";

const StyledRecurringFooter = styled(Flex)`
  text-align: center;
`;

const RecurringFooter = ({ purchase, membership }) => {
  const { _t } = useTranslate();
  const profileId = purchase.recurring_profile_id;

  return (
    <StyledRecurringFooter $direction="column" $gap="0.5rem">
      <Button as={Link} variant="outline" title="view_recurring_profile" to={ROUTES.CLIENT_RECURRING_PROFILE.replace(":id", profileId)} />
      <Text size="small">{_t("Automatic renew for")} <FormatPrice value={membership.recurring_price} currency={membership.currency} /></Text>
    </StyledRecurringFooter>
  );
};

const ClientMembershipItem = React.memo(({ purchase }) => {
  const { _t } = useTranslate();
  const membership = purchase.membership || {};
  const servicesInfo = membership.services_info || [];
  const servicesCount = servicesInfo.length;
  const servicesNamesNode = servicesInfo.map((s) => s.name).join(", ")
  const sessionsRest = membership.is_unlimited ? _t("Unlimited") : purchase.rest || 0;
  const sessionsLabel = `${_t("sessions remaining")}: ${sessionsRest}`;
  const hasActiveRecurringProfile =
    purchase.recurring_profile_status === RECURRING_PROFILE_STATUS.ACTIVE && purchase.recurring_profile_id;

  const periodNode = (
    <Text color="dark" as="span">
      <FormatDatePeriod from={purchase.period_start} to={purchase.period_end} format={FormatDatePeriod.FORMAT.DATE} />
    </Text>
  );

  const includes = [
    servicesCount > 0 && {
      icon: <Icon.CardsStack />,
      label: _t("Attached services: %count%", { count: servicesCount }),
      name: servicesNamesNode,
    },
    { icon: <Icon.CalendarDetailed />, name: periodNode, label: "Valid From - To" },
    { icon: <Icon.FileList />, name: sessionsLabel },
  ].filter(Boolean);

  return (
    <PluginCard
      disabled={!purchase.can_be_used}
      name={membership.name}
      description={membership.description}
      picture={membership.picture_preview}
      fallbackIcon={<Icon.MembershipItem size={87} color="iconStroke" strokeWidth={0.5}  />}
      includes={includes}
      includeLimit={3}
      footer={hasActiveRecurringProfile ? <RecurringFooter purchase={purchase} membership={membership} /> : null}
    />
  );
});

export default ClientMembershipItem;
